import type { ComponentType } from 'react';

import { action, autorun, makeObservable, observable } from 'mobx';

import { SOCKET_SOF_PUSHED } from '../../config/constants';
import { SofStatus } from '../../features/sof/enums';
import type { SofLoginResult } from '../../features/sof/types';
import { getRequest, postRequest } from '../../services/api';
import { logToNewRelic } from '../../services/debug';
import { resolveFlag } from '../../services/launchDarkly';
import { socket } from '../../services/socket';
import type { ApiResponse } from '../../services/types';
import type { FeatureFetchStatus, IFeatureManagerStore } from '../feature-manager/types';
import type { RootStore } from '../RootStore';

export class SofStore implements IFeatureManagerStore<'SOF'> {
  private static readonly STATUSES_REQUIRING_POPUP = new Set([SofStatus.INIT, SofStatus.REQUESTED]);

  private readonly rootStore: RootStore;
  private shouldShowSofPopup = false;
  private currentSofStatusReq: Promise<boolean> | null = null;
  private accessTokenReq: Promise<string> | null = null;

  public dialogTypes = ['SOF'] as const;
  public SofReactionComponent: ComponentType | null = null;

  @observable
  public accessToken: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    const pushSofHandler = this.handlePushedSof.bind(this);

    autorun(() => {
      if (this.rootStore.authStore.authorized) {
        socket.on(SOCKET_SOF_PUSHED, pushSofHandler);
      } else {
        socket.off(SOCKET_SOF_PUSHED, pushSofHandler);
        this.cleanup();
      }
    });
  }

  @action
  public cleanup() {
    this.shouldShowSofPopup = false;
    this.currentSofStatusReq = null;
    this.accessTokenReq = null;
    this.accessToken = null;
  }

  private handleStatus(sofStatus: SofStatus) {
    this.shouldShowSofPopup = SofStore.STATUSES_REQUIRING_POPUP.has(sofStatus);
  }

  private handlePushedSof() {
    this.shouldShowSofPopup = true;
    this.rootStore.featureManagerStore.addDialogsToSequenceQueue(['SOF']);
  }

  public async logIn() {
    if (this.accessTokenReq) {
      return this.accessTokenReq;
    }

    const req = postRequest<ApiResponse<SofLoginResult>>('/sof/log-in')
      .then((res) => {
        switch (res.data.data.type) {
          case 'initByPlayer':
          case 'proceedByPlayer':
            this.accessToken = res.data.data.payload.authToken;
            return res.data.data.payload.authToken;
          default:
            logToNewRelic(
              'SOF-FE - unexpected login result',
              `SOF-FE - unexpected login result: ${JSON.stringify(res.data)}`,
              'error',
            );
            throw new Error(`Unexpected login result: ${JSON.stringify(res.data)}`);
        }
      })
      .catch((error) => {
        logToNewRelic('SOF-FE - login error', `SOF-FE - login error: ${JSON.stringify(error)}`, 'error');
        throw error;
      })
      .finally(() => {
        this.accessTokenReq = null;
      });

    this.accessTokenReq = req;
    return req;
  }

  startDialogs() {
    if (this.SofReactionComponent && this.shouldShowSofPopup) {
      this.rootStore.portalStore.openPortal(this.SofReactionComponent, {
        beforeClose: () => {
          this.cleanup();
          this.rootStore.featureManagerStore.onDialogFinished('SOF');
        },
      });

      return true;
    }

    return false;
  }

  private fetchCurrentSofStatus() {
    const sofEnabled = resolveFlag('sof-enabled', false);

    if (!sofEnabled) {
      return;
    }

    if (this.currentSofStatusReq) {
      return this.currentSofStatusReq;
    }

    const req = getRequest<ApiResponse<{ status: SofStatus }>>('/sof/situation')
      .then((res) => {
        if (!this.rootStore.authStore.authorized || !this.currentSofStatusReq) {
          return false;
        }

        this.handleStatus(res.data.data.status);
        return true;
      })
      .catch((error) => {
        logToNewRelic(
          'SOF-FE - failed to fetch current SOF status',
          `SOF-FE - failed to fetch current SOF status: ${JSON.stringify(error)}`,
          'error',
        );

        return false;
      })
      .finally(() => {
        this.currentSofStatusReq = null;
      });

    this.currentSofStatusReq = req;

    return req;
  }

  async fetch(): Promise<FeatureFetchStatus> {
    await this.fetchCurrentSofStatus();
    return 'READY';
  }
}
